import React, { useCallback, useContext, useMemo } from "react";

import { faTriangleExclamation } from "@awesome.me/kit-4c83d50d0d/icons/classic/solid";
import { faRecordSlash } from "@awesome.me/kit-4c83d50d0d/icons/kit/custom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

// eslint-disable-next-line max-len
import { useWillBeRecordedNoCacheQuery } from "~/account/Copilot/AutoRecordRules/modal/WillBeRecordedNoCache";
import MeetingPhaseContext from "~/stream/MeetingPhaseContext";
import { useRecordingErrorIndicatorFeatureFlagOn } from "~/stream/utils";
import { Tooltip } from "~/ui";

import { RECORDING_RESTRICTED_MESSAGE } from "./RecordingControls/StartAndAutoRecordingControls";

import styles from "./RecordingErrorOrRestrictedIndicator.module.css";

function RecordingRestrictedIndicator() {
	return (
		<Tooltip
			placement="top"
			maxWidth={250}
			className={styles.tooltip}
			content={
				<div className={styles.tooltipContent}>
					<div className={styles.tooltipHeader}>Recording prohibited</div>
					<div>{RECORDING_RESTRICTED_MESSAGE}</div>
				</div>
			}
		>
			<FontAwesomeIcon icon={faRecordSlash} className={styles.restrictedIndicator} />
		</Tooltip>
	);
}

export default function RecordingErrorOrRestrictedIndicator({ eventGuid }: { eventGuid?: string }) {
	const errorIndicatorFeatureFlagOn = useRecordingErrorIndicatorFeatureFlagOn();
	const { previousErrorIndicator: errorIndicator, viewerId, hasMeetingRecordings } = useContext(MeetingPhaseContext);
	const willBeRecorded = useWillBeRecordedNoCacheQuery(eventGuid);

	const formatName = useCallback((name: string) => {
		// We only want the first name and the first character of the last name
		const names = name.split(" ");
		return `${names[0]} ${names[names.length - 1][0]}`; // Incase we have a middle name
	}, []);

	const formatDate = useCallback((date: Date) => {
		// Example: Oct 28 at 2:01 PM

		// Use client timezone to format the date
		const monthAndDay = date.toLocaleString(undefined, { month: "short", day: "numeric" });
		const time = date.toLocaleString(undefined, { hour: "numeric", minute: "2-digit" });
		return `${monthAndDay} at ${time}`;
	}, []);

	const tooltipContent = useMemo(() => {
		if (!errorIndicator) return null;
		const message =
			errorIndicator.type == "cancelled" && errorIndicator.cancelledBy
				? `Cancelled in Fellow by ${formatName(errorIndicator.cancelledBy.fullName)}`
				: errorIndicator.message
					? errorIndicator.message
					: "An error occurred while recording the meeting";
		return (
			<div className={styles.errorIndicatorTooltipContent}>
				<span className={styles.errorIndicatorMessage}>{message}</span>
				{errorIndicator.updatedAt && (
					<span className={styles.errorIndicatorUpdatedAt}>{formatDate(errorIndicator.updatedAt)}</span>
				)}
			</div>
		);
	}, [errorIndicator, formatName, formatDate]);

	if (willBeRecorded === "RESTRICTED") return <RecordingRestrictedIndicator />;

	if (!errorIndicator || hasMeetingRecordings) return null;

	if (errorIndicator.type === "cancelled" && errorIndicator.cancelledBy?.id === viewerId) {
		return null;
	}

	if (!errorIndicatorFeatureFlagOn) return null;

	return (
		<div className={styles.errorIndicator}>
			<Tooltip placement="top" content={tooltipContent}>
				<FontAwesomeIcon icon={faTriangleExclamation} />
			</Tooltip>
		</div>
	);
}
